// Icon Service for platform SVG management
let platformSvgs = {};
let isLoaded = false;

/**
 * Initialize the icon service by loading all platform SVGs
 */
export const initializeIconService = async () => {
    if (isLoaded) return;

    try {
        // Import all SVGs as raw text from main platforms directory
        const modules = import.meta.glob("@/assets/icons/platforms/*.svg", {
            as: "raw",
            eager: true,
        });

        for (const path in modules) {
            const name = path
                .split("/")
                .pop()
                .replace(".svg", "")
                .toLowerCase();
            const loader = modules[path];
            const svgContent =
                typeof loader === "function" ? await loader() : loader;

            // Clean and prepare SVG
            let cleanSvg = svgContent
                .replace(/width="[^"]*"/g, "") // Remove width
                .replace(/height="[^"]*"/g, "") // Remove height
                .replace(/<svg/, '<svg width="100%" fill="currentColor"'); // Add our attributes

            platformSvgs[name] = cleanSvg;
        }

        // Import all SVGs from mono subdirectory
        const monoModules = import.meta.glob(
            "@/assets/icons/platforms/mono/*.svg",
            {
                as: "raw",
                eager: true,
            }
        );

        for (const path in monoModules) {
            const name = path
                .split("/")
                .pop()
                .replace(".svg", "")
                .toLowerCase();
            const monoLoader = monoModules[path];
            const svgContent =
                typeof monoLoader === "function"
                    ? await monoLoader()
                    : monoLoader;

            // Clean and prepare SVG
            let cleanSvg = svgContent
                .replace(/width="[^"]*"/g, "") // Remove width
                .replace(/height="[^"]*"/g, "") // Remove height
                .replace(/<svg/, '<svg width="100%" fill="currentColor"'); // Add our attributes

            platformSvgs[name + "-mono"] = cleanSvg;
        }

        isLoaded = true;
    } catch (error) {
        console.error("Failed to load platform icons:", error);
    }
};

// Counter for generating unique IDs
let idCounter = 0;

/**
 * Make SVG IDs unique by replacing all id="..." and url(#...) references
 * with unique versions to prevent conflicts when the same SVG is rendered multiple times
 * @param {string} svg - SVG content
 * @returns {string} SVG with unique IDs
 */
const makeIdsUnique = (svg) => {
    if (!svg) return svg;

    const uniqueSuffix = `_${++idCounter}`;
    const idMap = {};

    // Find all id="..." definitions and create a mapping
    const idDefRegex = /id="([^"]+)"/g;
    let match;
    while ((match = idDefRegex.exec(svg)) !== null) {
        const originalId = match[1];
        idMap[originalId] = originalId + uniqueSuffix;
    }

    // Replace all id definitions and url(#...) references
    let result = svg;
    for (const [originalId, newId] of Object.entries(idMap)) {
        // Replace id definition
        result = result.replace(
            new RegExp(`id="${originalId}"`, "g"),
            `id="${newId}"`
        );
        // Replace url(#...) references
        result = result.replace(
            new RegExp(`url\\(#${originalId}\\)`, "g"),
            `url(#${newId})`
        );
        // Replace xlink:href references
        result = result.replace(
            new RegExp(`xlink:href="#${originalId}"`, "g"),
            `xlink:href="#${newId}"`
        );
        // Replace href references (for newer SVGs)
        result = result.replace(
            new RegExp(`href="#${originalId}"`, "g"),
            `href="#${newId}"`
        );
    }

    return result;
};

/**
 * Get SVG content for a given platform slug
 * @param {string} slug - Platform slug
 * @param {string} type - Icon type: 'default', 'logotype', or 'mono'
 * @returns {string} SVG content or empty string if not found
 */
export const getSvgForPlatform = (slug, type = "default") => {
    if (!slug) return "";

    let svg;
    switch (type) {
        case "logotype":
            svg = platformSvgs[slug + "-logotype"] || "";
            break;
        case "mono":
            svg = platformSvgs[slug + "-mono"] || "";
            break;
        case "default":
        default:
            svg = platformSvgs[slug] || "";
    }

    // Make IDs unique to prevent conflicts when same SVG is rendered multiple times
    return makeIdsUnique(svg);
};

/**
 * Check if icons are loaded
 * @returns {boolean}
 */
export const isIconServiceReady = () => isLoaded;

// Auto-initialize when service is imported
initializeIconService();
